import { Dispatch, PayloadAction, createSlice } from '@reduxjs/toolkit';
import keyBy from 'lodash/keyBy';
import { CustomStatus, UserStatus } from 'src/@types/user-status';
import {
  IWorkspaceInfo,
  IWorkspaceTeam,
  IWorkspaceUser,
} from 'src/@types/workspace-info';
import { TeamRequests } from 'src/requests/team.requests';
import { WorkspaceRequests } from 'src/requests/workspace.requests';

export type IWorkspaceState = {
  isLoading: boolean;
  error: Error | string | null;
  workspace: IWorkspaceInfo;
  usersById: Record<string, IWorkspaceUser>;
  teamsById: Record<string, IWorkspaceTeam>;
};

const initialState: IWorkspaceState = {
  isLoading: false,
  error: null,
  usersById: {},
  teamsById: {},
  workspace: {
    _id: '',
    slug: '',
    pendingInvites: [],
    users: [],
    name: '',
    owner: '',
    shortName: '',
    featureSettings: { smartTodos: false },
  } as IWorkspaceInfo,
};

const slice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },

    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getWorkspaceSuccess(
      state,
      action: PayloadAction<{
        workspace: IWorkspaceInfo;
        teams: IWorkspaceTeam[];
      }>,
    ) {
      state.isLoading = false;
      const { workspace, teams } = action.payload;
      state.workspace = workspace;
      state.usersById = keyBy(workspace.users, '_id');
      state.teamsById = keyBy(teams, '_id');
    },

    inviteWorkspaceMembersSuccess(state, action) {
      state.isLoading = false;
      state.workspace.pendingInvites = action.payload;
    },

    updateWorkspaceMemberSuccess(state, action) {
      state.isLoading = false;
      state.workspace.users = state.workspace.users.map((user) => {
        if (user._id === action.payload._id) {
          return action.payload;
        }
        return user;
      });
    },

    deleteWorkspaceMemberSuccess(state, action) {
      const userId = action.payload;
      state.workspace.users = state.workspace.users.filter(
        (user) => user._id !== userId,
      );
    },

    updateUserStatus(state, action) {
      const { userId, status } = action.payload;
      state.workspace.users = state.workspace.users.map((user) =>
        user._id === userId
          ? { ...user, status, lastAccess: new Date().toISOString() }
          : user,
      );
    },

    updateUserCustomStatus(state, action) {
      const { userId, customStatus } = action.payload;
      state.workspace.users = state.workspace.users.map((user) =>
        user._id === userId
          ? { ...user, customStatus, lastAccess: new Date().toISOString() }
          : user,
      );
    },
  },
});

export const workspaceReducer = slice.reducer;

export function getWorkspaceInfo() {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const [workspace, teams] = await Promise.all([
        WorkspaceRequests.getWorkspaceInfo(),
        TeamRequests.getTeams(),
      ]);
      dispatch(slice.actions.getWorkspaceSuccess({ workspace, teams }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function inviteWorkspaceMembers(emails: string[]) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const workspaceInfo = await WorkspaceRequests.inviteEmails(emails);
      dispatch(
        slice.actions.inviteWorkspaceMembersSuccess(
          workspaceInfo.pendingInvites,
        ),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateUserStatus(userId: string, status: UserStatus) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateUserStatus({ userId, status }));
  };
}

export function updateUserCustomStatus(
  userId: string,
  customStatus: CustomStatus,
) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateUserCustomStatus({ userId, customStatus }));
  };
}
